import { defineMessages, useIntl } from 'react-intl';

import { FlattenedOrderStatuses } from '@sg/graphql-schema';

import type { AddressType } from '@garnish/location';

// ────────────────────────────────────────────────────────────────────────────────

export const useProgressNodes = (
  orderType?: AddressType,
  orderStatus?: FlattenedOrderStatuses,
): ProgressNode[] => {
  const { formatMessage } = useIntl();

  if (orderType === 'pickup') {
    return [
      {
        id: FlattenedOrderStatuses.Received,
        label: formatMessage(messages.progressPickupReceived),
        completed: [
          FlattenedOrderStatuses.Received,
          FlattenedOrderStatuses.Preparing,
          FlattenedOrderStatuses.Completed,
        ].includes(orderStatus ?? FlattenedOrderStatuses.Failed),
      },
      {
        id: FlattenedOrderStatuses.Preparing,
        label: formatMessage(messages.progressPickupPreparing),
        completed: [FlattenedOrderStatuses.Preparing, FlattenedOrderStatuses.Completed].includes(
          orderStatus ?? FlattenedOrderStatuses.Failed,
        ),
      },
      {
        id: FlattenedOrderStatuses.Completed,
        label: formatMessage(messages.progressPickupCompleted),
        completed: [FlattenedOrderStatuses.Completed].includes(
          orderStatus ?? FlattenedOrderStatuses.Failed,
        ),
      },
    ];
  }

  if (orderType === 'outpost') {
    return [
      {
        id: FlattenedOrderStatuses.Received,
        label: formatMessage(messages.progressOutpostReceived),
        completed: [
          FlattenedOrderStatuses.Received,
          FlattenedOrderStatuses.Delivering,
          FlattenedOrderStatuses.Completed,
        ].includes(orderStatus ?? FlattenedOrderStatuses.Failed),
      },
      {
        id: FlattenedOrderStatuses.Delivering,
        label: formatMessage(messages.progressOutpostDelivering),
        completed: [FlattenedOrderStatuses.Delivering, FlattenedOrderStatuses.Completed].includes(
          orderStatus ?? FlattenedOrderStatuses.Failed,
        ),
      },
      {
        id: FlattenedOrderStatuses.Completed,
        label: formatMessage(messages.progressOutpostCompleted),
        completed: [FlattenedOrderStatuses.Completed].includes(
          orderStatus ?? FlattenedOrderStatuses.Failed,
        ),
      },
    ];
  }

  if (orderType === 'delivery') {
    return [
      {
        id: FlattenedOrderStatuses.Received,
        label: formatMessage(messages.progressDeliveryReceived),
        completed: [
          FlattenedOrderStatuses.Received,
          FlattenedOrderStatuses.Preparing,
          FlattenedOrderStatuses.ReadyForCourier,
          FlattenedOrderStatuses.Delivering,
          FlattenedOrderStatuses.Completed,
        ].includes(orderStatus ?? FlattenedOrderStatuses.Failed),
      },
      {
        id: FlattenedOrderStatuses.Preparing,
        label: formatMessage(messages.progressDeliveryPreparing),
        completed: [
          FlattenedOrderStatuses.Preparing,
          FlattenedOrderStatuses.ReadyForCourier,
          FlattenedOrderStatuses.Delivering,
          FlattenedOrderStatuses.Completed,
        ].includes(orderStatus ?? FlattenedOrderStatuses.Failed),
      },
      {
        id: FlattenedOrderStatuses.ReadyForCourier,
        label: formatMessage(messages.progressDeliveryCourier),
        completed: [
          FlattenedOrderStatuses.ReadyForCourier,
          FlattenedOrderStatuses.Delivering,
          FlattenedOrderStatuses.Completed,
        ].includes(orderStatus ?? FlattenedOrderStatuses.Failed),
      },
      {
        id: FlattenedOrderStatuses.Delivering,
        label: formatMessage(messages.progressDeliveryDelivering),
        completed: [FlattenedOrderStatuses.Delivering, FlattenedOrderStatuses.Completed].includes(
          orderStatus ?? FlattenedOrderStatuses.Failed,
        ),
      },
    ];
  }

  return [];
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  progressPickupReceived: {
    defaultMessage: 'Received',
    description: 'Order Status | Info | Progress label for pickup received',
  },
  progressPickupPreparing: {
    defaultMessage: 'Preparing',
    description: 'Order Status | Info | Progress label for pickup preparing',
  },
  progressPickupCompleted: {
    defaultMessage: 'Complete',
    description: 'Order Status | Info | Progress label for pickup completed',
  },
  progressOutpostReceived: {
    defaultMessage: 'Received',
    description: 'Order Status | Info | Progress label for outpost received',
  },
  progressOutpostDelivering: {
    defaultMessage: 'Delivering',
    description: 'Order Status | Info | Progress label for outpost delivering',
  },
  progressOutpostCompleted: {
    defaultMessage: 'Complete',
    description: 'Order Status | Info | Progress label for outpost completed',
  },
  progressDeliveryReceived: {
    defaultMessage: 'Received',
    description: 'Order Status | Info | Progress label for delivery received',
  },
  progressDeliveryPreparing: {
    defaultMessage: 'Preparing',
    description: 'Order Status | Info | Progress label for delivery preparing',
  },
  progressDeliveryCourier: {
    defaultMessage: 'Ready for courier',
    description: 'Order Status | Info | Progress label for delivery ready for courier',
  },
  progressDeliveryDelivering: {
    defaultMessage: 'Delivering',
    description: 'Order Status | Info | Progress label for delivery delivering',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ProgressNode = Readonly<{
  id: FlattenedOrderStatuses;
  label: string;
  completed: boolean;
}>;
